import { useFetcher } from 'react-router';
import { useAuthenticityToken } from 'remix-utils/csrf/react';

import { Icon } from '~/components/ui/icon';
import { StatusButton } from '~/components/ui/status-button';
import { CreateClassRsvpError } from '~/domain/gym-classes/models/create-class-rsvp-error';
import { type GymClass } from '~/domain/gym-classes/models/gym-class';
import { type Gym } from '~/domain/gyms/models/gym';

import { type action as createAction } from '../classes+/$gymClassId.rsvp';
import { type action as deleteAction } from '../classes+/$gymClassId.rsvp.$rsvpId';

export interface RsvpButtonProps {
  gymClassName: string;
  gymId: Gym['id'];
  gymClassId: GymClass['id'];
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM:SS
  endTime: string; // HH:MM:SS
  rsvpId: string | null;
  disabled?: boolean;
}

const hasError = (data: string | { error: string } | null | undefined, error: string) =>
  data != null && typeof data === 'object' && data.error === error;

export function RsvpButton({
  gymClassName,
  gymId,
  gymClassId,
  date,
  startTime,
  endTime,
  rsvpId,
  disabled = false,
}: RsvpButtonProps) {
  const createFetcher = useFetcher<typeof createAction>();
  const deleteFetcher = useFetcher<typeof deleteAction>();
  const csrf = useAuthenticityToken();

  const hasRsvp = rsvpId || hasError(createFetcher.data, CreateClassRsvpError.RsvpExists);
  const isPastOrOngoing = hasError(createFetcher.data, CreateClassRsvpError.RsvpToPastEvent);

  const isIdle = createFetcher.state === 'idle' && deleteFetcher.state === 'idle';

  const createRsvp = () => {
    createFetcher.submit(
      { csrf, date, startTime, endTime, gymClassName },
      {
        method: 'POST',
        action: `/gym/${gymId}/classes/${gymClassId}/rsvp`,
        preventScrollReset: true,
      },
    );
  };

  const deleteRsvp = (rsvpId: string) => {
    deleteFetcher.submit(
      { csrf, gymClassName },
      {
        method: 'DELETE',
        action: `/gym/${gymId}/classes/${gymClassId}/rsvp/${rsvpId}`,
        preventScrollReset: true,
      },
    );
  };

  const handleRsvp = () => {
    if (!rsvpId) {
      createRsvp();
    } else {
      deleteRsvp(rsvpId);
    }
  };

  return (
    <StatusButton
      variant={hasRsvp ? 'outline' : 'default'}
      className="w-full"
      onClick={handleRsvp}
      disabled={disabled || !isIdle || isPastOrOngoing}
      type="submit"
      color="primary"
      status={!isIdle ? 'pending' : 'idle'}
    >
      {hasRsvp ? (
        <>
          Going&nbsp;
          <Icon name="check" />
        </>
      ) : (
        'RSVP'
      )}
    </StatusButton>
  );
}
